
import { curry, equals, expect, is, test } from "@benchristel/taste"
import analytics from "./analytics"
import { DebugController } from "./DebugController"

import type { Path } from "./lib/Path"
import type { NavigateTo } from "./lib/useURLHash"
import { MapController } from "./MapController"
import { PollController } from "./PollController"
import { PollCreationController } from "./PollCreationController"
import { PollProvider } from "./PollProvider"
import { ResultsController } from "./ResultsController"

function pollID(path: string): any {
  return path.split("/")[1]
}

test("pollID", {
  "given a path, returns only the poll ID"() {
    expect(pollID("/the-poll-id/results"), is, "the-poll-id")
  },
})

export function route(path: string, navigateTo: NavigateTo): JSX.Element {
  return page(path)(navigateTo)
}

function page(path: string): (arg: NavigateTo) => JSX.Element {
  analytics.page()
  switch (true) {
    case /^\/[^\/]+$/.test(path):
      return ballotPage(pollID(path))
    case /^\/?$/.test(path):
      return homePage
    case /^\/[^\/]+\/results$/.test(path):
      return resultsPage(pollID(path))
    case /^\/[^\/]+\/debug$/.test(path):
      return debugPage(pollID(path))
    case /^\/[^\/]+\/map$/.test(path):
      return mapPage(pollID(path))
  }
  return notFoundPage
}

const ballotPage = curry(function (pollID: string, _: any) {
  return (
    <PollProvider key={pollID} pollID={pollID}>
      {(pollBus, profileID, setProfileID) => (
          <PollController activeProfile={profileID} pollBus={pollBus} setProfileID={setProfileID}/>
      )}
    </PollProvider>
  )
}, "ballotPage")

function homePage(navigateTo: NavigateTo): JSX.Element {
  return (
    <PollCreationController
      onPollCreated={(pollID) => navigateTo(`/${pollID}`)}
    />
  )
}

const resultsPage = curry(function (pollID: string, _: any) {
  return (
    <PollProvider key={pollID} pollID={pollID}>
      {(pollBus) => <ResultsController pollBus={pollBus} />}
    </PollProvider>
  )
}, "resultsPage")

const debugPage = curry(
  (pollID: string, _: any) => (
    <PollProvider key={pollID} pollID={pollID}>
      {(pollBus) => <DebugController pollBus={pollBus} />}
    </PollProvider>
  ),
  "debugPage",
)

const mapPage = curry(
  (pollID: string, _: any) => (
    <PollProvider key={pollID} pollID={pollID}>
      {(pollBus) => <MapController pollBus={pollBus} />}
    </PollProvider>
  ),
  "mapPage",
)

test("page", {
  "when given a poll route returns the poll page"() {
    expect(page("/poll-id"), equals, ballotPage("poll-id"))
  },
  "when given the root route, returns the home page"() {
    expect(page("/"), equals, homePage)
  },
  "when given no path, returns the home page"() {
    expect(page(""), equals, homePage)
  },
  "when given random nonsense, returns not-found page"() {
    expect(page("results"), equals, notFoundPage)
    expect(page("bar"), equals, notFoundPage)
  },
  "when given a results path, returns a results page"() {
    expect(page("/poll-id/results"), equals, resultsPage("poll-id"))
  },
  "when given a debug path, returns a debug page"() {
    expect(page("/poll-id/debug"), equals, debugPage("poll-id"))
  },
})

function notFoundPage() {
  return <p>Not Found</p>
}

export function ballotPath(pollID: string): Path {
  return `/#/${pollID}`
}

export function pollResultsPath(pollID: string): Path {
  return `/#/${pollID}/results`
}

export function pollMapPath(pollID: string): Path {
  return `/#/${pollID}/map`
}


